/* eslint-disable react-hooks/refs */
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, X, Search, Check, Timer } from 'lucide-react'
import { isBugsnapUiEvent } from '@features/bug-report/bugsnapUi'
import './SearchableDropdown.css'

export interface SearchableDropdownOption {
  value: string
  label: React.ReactNode
  labelStr?: string
  disabled?: boolean
}

export interface SearchableDropdownProps {
  options: SearchableDropdownOption[]
  value: string
  placeholder?: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  searchPlaceholder?: string
  maxHeight?: string
  /** Minimum characters before showing search results (default: 0) */
  minSearchLength?: number
  /** Maximum number of visible options (for performance with large lists) */
  maxVisibleOptions?: number
  /** Whether to show the clear button (default: true) */
  clearable?: boolean
  /** Callback when search query changes for server-side search */
  onSearchChange?: (query: string) => void
  /** Called each time the menu opens (e.g. to refetch fresh options) */
  onOpen?: () => void
  /** Show loading indicator when true */
  isLoading?: boolean
  /** Whether to completely omit the search input UI (for small lists) */
  hideSearch?: boolean
  /** Handle scrolling for infinite pagination */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  /** Is it loading more items? */
  isLoadingMore?: boolean
  /** Are there more items to load? */
  hasMore?: boolean
  /** Always open the menu upward instead of auto-detecting */
  forceUpward?: boolean
  /** Whether to render the menu in a portal at document.body (default: false) */
  usePortal?: boolean
  /** Tooltip title for the dropdown trigger button */
  title?: string
  /** Hide the chevron arrow (e.g. when the dropdown is locked to a single value) */
  hideArrow?: boolean
  /** Open the menu immediately on mount (e.g. when revealed by an "add" button) */
  defaultOpen?: boolean
  /** Called whenever the menu closes without a selection (outside click, Escape) */
  onClose?: () => void
}

function cleanTooltip(title: string | undefined): string | undefined {
  if (!title) return undefined
  const regex = /\s*\(pending_review\)/gi
  if (regex.test(title)) {
    return undefined
  }
  return title
}

function renderOptionLabel(label: React.ReactNode): React.ReactNode {
  if (typeof label === 'string') {
    const regex = /\s*\(pending_review\)/gi
    if (regex.test(label)) {
      const cleanText = label.replace(regex, '')
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span>{cleanText}</span>
          <span
            title="Pending review"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-muted, #94a3b8)',
              cursor: 'pointer',
            }}
          >
            <Timer size={12} />
          </span>
        </span>
      )
    }
  }
  return label
}

function resolveStackingZIndex(startEl: HTMLElement | null, baseZ = 90): number {
  let resolvedZ = baseZ
  let el: HTMLElement | null = startEl
  while (el && el !== document.body) {
    const z = window.getComputedStyle(el).zIndex
    if (z && z !== 'auto') {
      const parsed = Number.parseInt(z, 10)
      if (!Number.isNaN(parsed) && parsed > resolvedZ) {
        resolvedZ = parsed
      }
    }
    el = el.parentElement
  }
  return resolvedZ < 101 ? 90 : resolvedZ + 50
}

interface SearchableDropdownMenuMetrics {
  openUpward: boolean
  cappedMaxHeight: string
  optionsMaxHeight: string
  menuWidth: number
  left: number
}

function computeSearchableDropdownMenuMetrics(
  rect: DOMRect,
  {
    forceUpward,
    hideSearch,
    maxHeight,
    optionCount,
  }: {
    forceUpward: boolean
    hideSearch: boolean
    maxHeight: string
    optionCount: number
  },
): SearchableDropdownMenuMetrics {
  const MENU_GAP = 4
  const VIEWPORT_PADDING = 8
  const parsedMaxHeight = Number.parseInt(maxHeight, 10) || 300
  const searchHeader = hideSearch ? 0 : 48
  const estimatedHeight = Math.min(parsedMaxHeight, optionCount * 36 + searchHeader + 8)

  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING
  const spaceAbove = rect.top - VIEWPORT_PADDING
  const openUpward = forceUpward || (spaceBelow < estimatedHeight && spaceAbove > spaceBelow)

  const availableSpace = Math.max(80, (openUpward ? spaceAbove : spaceBelow) - MENU_GAP)
  const cappedHeight = Math.min(parsedMaxHeight, availableSpace)
  const cappedMaxHeight = `${cappedHeight}px`
  const optionsMaxHeight = `${Math.max(60, cappedHeight - searchHeader - 8)}px`
  const menuWidth = rect.width
  const left = Math.max(
    VIEWPORT_PADDING,
    Math.min(rect.left, window.innerWidth - menuWidth - VIEWPORT_PADDING),
  )

  return {
    openUpward,
    cappedMaxHeight,
    optionsMaxHeight,
    menuWidth,
    left,
  }
}

function resolveSearchableMenuTop(
  usePortal: boolean,
  menuPosition: { top: number; openUpward: boolean },
): number | string {
  if (usePortal) return menuPosition.top
  if (menuPosition.openUpward) return 'auto'
  return 'calc(100% + 4px)'
}

interface SearchableDropdownMenuBodyArgs {
  isLoading: boolean
  filteredOptionsLength: number
  menuPosition: { optionsMaxHeight: string }
  visibleOptions: SearchableDropdownOption[]
  value: string
  focusedIndex: number
  isLoadingMore: boolean
  handleListScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  handleSelect: (optionValue: string) => void
  handleKeyDown: (e: React.KeyboardEvent) => void
}

function renderSearchableDropdownMenuBody({
  isLoading,
  filteredOptionsLength,
  menuPosition,
  visibleOptions,
  value,
  focusedIndex,
  isLoadingMore,
  handleListScroll,
  handleSelect,
  handleKeyDown,
}: SearchableDropdownMenuBodyArgs): React.ReactNode {
  if (isLoading && visibleOptions.length === 0) {
    return <div className="searchable-dropdown__no-results">Loading...</div>
  }
  if (filteredOptionsLength === 0) {
    return <div className="searchable-dropdown__no-results">No results found</div>
  }

  return (
    <div
      id="searchable-dropdown-listbox"
      className="searchable-dropdown__options"
      role="listbox"
      onScroll={handleListScroll}
      style={{ maxHeight: menuPosition.optionsMaxHeight }}
    >
      {visibleOptions.map((option, index) => (
        <button
          key={option.value || `option-${index}`}
          id={`option-${index}`}
          ref={(el) => {
            if (index === focusedIndex && el) {
              el.scrollIntoView({ block: 'nearest' })
            }
          }}
          type="button"
          className={`searchable-dropdown__option ${option.value === value ? 'searchable-dropdown__option--selected' : ''} ${index === focusedIndex ? 'searchable-dropdown__option--focused' : ''} ${option.disabled ? 'searchable-dropdown__option--disabled' : ''}`}
          onClick={() => !option.disabled && handleSelect(option.value)}
          onKeyDown={handleKeyDown}
          disabled={option.disabled}
          tabIndex={-1}
          role="option"
          aria-selected={option.value === value}
          title={cleanTooltip(option.labelStr || (typeof option.label === 'string' ? option.label : undefined))}
        >
          <span className="searchable-dropdown__option-label">{renderOptionLabel(option.label)}</span>
          {option.value === value && (
            <Check size={14} className="searchable-dropdown__option-check" />
          )}
        </button>
      ))}
      {isLoadingMore && (
        <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading more...
        </div>
      )}
    </div>
  )
}

/**
 * SearchableDropdown - A dropdown component with built-in search functionality
 *
 * Optimized for large lists (1000s of items) with client-side filtering.
 * Only renders visible options for better performance.
 *
 * @example
 * ```tsx
 * <SearchableDropdown
 *   options={users.map(u => ({ value: u.id, label: u.display_name }))}
 *   value={selectedUserId}
 *   onChange={setSelectedUserId}
 *   placeholder="Select a user..."
 *   searchPlaceholder="Search users..."
 *   maxVisibleOptions={50}
 *   title="Select a user dropdown"
 * />
 * ```
 */
export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  placeholder = 'Select...',
  onChange,
  disabled = false,
  className = '',
  searchPlaceholder = 'Search...',
  maxHeight = '300px',
  minSearchLength = 0,
  maxVisibleOptions = 50,
  clearable = true,
  onSearchChange,
  onOpen,
  isLoading = false,
  hideSearch = false,
  onScroll,
  isLoadingMore = false,
  forceUpward = false,
  usePortal = false,
  title,
  hideArrow = false,
  defaultOpen = false,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [internalSearchQuery, setInternalSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight: string
    optionsMaxHeight: string
    openUpward: boolean
    zIndex: number
  } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selectedOption = options.find((option) => option.value === value)

  // Notify the parent when the menu closes (outside click, Escape, select…).
  // Only after it has actually been open, so consumers mounting with the
  // default closed state don't get a spurious call.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  const hasOpenedRef = useRef(defaultOpen)
  useEffect(() => {
    if (isOpen) {
      hasOpenedRef.current = true
    } else if (hasOpenedRef.current) {
      onCloseRef.current?.()
    }
  }, [isOpen])

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    // If onSearchChange is provided, assume remote filtering and return all options
    if (onSearchChange) return options

    if (!internalSearchQuery || internalSearchQuery.length < minSearchLength) {
      return options
    }

    const query = internalSearchQuery.toLowerCase()
    return options.filter(
      (option) => {
        const textToSearch = option.labelStr || (typeof option.label === 'string' ? option.label : '')
        return textToSearch.toLowerCase().includes(query) || option.value.toLowerCase().includes(query)
      }
    )
  }, [options, internalSearchQuery, minSearchLength, onSearchChange])

  // Client-side "scroll to load more": when the parent does NOT drive pagination
  // (no onScroll), reveal options incrementally as the user scrolls instead of
  // hard-capping at maxVisibleOptions. Server-driven mode is left untouched.
  const [clientLimit, setClientLimit] = useState(maxVisibleOptions)

  // Limit visible options for performance.
  const visibleOptions = useMemo(() => {
    if (onScroll) return filteredOptions
    return filteredOptions.slice(0, clientLimit)
  }, [filteredOptions, clientLimit, onScroll])

  // Reset the incremental window when the menu reopens or the query changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setClientLimit(maxVisibleOptions)
  }, [internalSearchQuery, isOpen, maxVisibleOptions])

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Preserve server-side pagination behaviour when the parent opts in.
    if (onScroll) {
      onScroll(e)
      return
    }
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setClientLimit((prev) =>
        prev < filteredOptions.length ? prev + maxVisibleOptions : prev,
      )
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFocusedIndex(-1)
  }, [internalSearchQuery, isOpen])

  // Close dropdown when clicking outside (trigger or portal menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Stay open while the user files a Bugsnap about this UI (see bugsnapUi).
      if (isBugsnapUiEvent(event.target, dropdownRef.current)) return
      const target = event.target as Node
      const insideTrigger = dropdownRef.current?.contains(target)
      const insideMenu = menuRef.current?.contains(target)
      if (!insideTrigger && !insideMenu) {
        setIsOpen(false)
        if (!onSearchChange) setInternalSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onSearchChange])

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        if (!onSearchChange) setInternalSearchQuery('')
        setTimeout(() => {
          triggerRef.current?.focus()
        }, 0)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onSearchChange])

  // Close dropdown on scroll outside the menu (keeps position accurate when portalled)
  useEffect(() => {
    if (!isOpen || !usePortal) return
    const handleScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return
      setIsOpen(false)
      if (!onSearchChange) setInternalSearchQuery('')
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen, onSearchChange, usePortal])

  // Close when the left nav rail expands so it is not obscured under the overlay.
  // Skip modals — centered modal menus should not vanish on rail hover.
  useEffect(() => {
    if (!usePortal || !isOpen) return
    const rail = document.querySelector('.nav-rail')
    if (!rail) return
    if (dropdownRef.current?.closest('.modal-overlay, [role="dialog"]')) return

    const syncWithNav = () => {
      if (rail.classList.contains('expanded')) {
        setIsOpen(false)
        if (!onSearchChange) setInternalSearchQuery('')
      }
    }

    syncWithNav()
    const observer = new MutationObserver(syncWithNav)
    observer.observe(rail, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [usePortal, isOpen, onSearchChange])

  const handleSelect = (optionValue: string) => {
    if (options.find((opt) => opt.value === optionValue)?.disabled) {
      return
    }
    // Match FilterDropdown: re-clicking the selected option clears it when clearable.
    onChange(clearable && optionValue === value ? '' : optionValue)
    setIsOpen(false)
    if (!onSearchChange) setInternalSearchQuery('')
    setTimeout(() => {
      triggerRef.current?.focus()
    }, 0)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    if (!onSearchChange) setInternalSearchQuery('')
  }

  const updateMenuPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect() || dropdownRef.current?.getBoundingClientRect()
    if (!rect) return

    const MENU_GAP = 4
    const optionCount = Math.min(
      filteredOptions.length > 0 ? filteredOptions.length : options.length,
      maxVisibleOptions,
    )
    const metrics = computeSearchableDropdownMenuMetrics(rect, {
      forceUpward,
      hideSearch,
      maxHeight,
      optionCount,
    })
    const portalZ = resolveStackingZIndex(dropdownRef.current?.parentElement ?? null)

    if (usePortal) {
      setMenuPosition({
        top: metrics.openUpward ? rect.top - MENU_GAP : rect.bottom + MENU_GAP,
        left: metrics.left,
        width: metrics.menuWidth,
        maxHeight: metrics.cappedMaxHeight,
        optionsMaxHeight: metrics.optionsMaxHeight,
        openUpward: metrics.openUpward,
        zIndex: portalZ,
      })
      return
    }

    setMenuPosition({
      top: metrics.openUpward
        ? rect.top + window.scrollY - MENU_GAP
        : rect.bottom + window.scrollY + MENU_GAP,
      left: rect.left + window.scrollX,
      width: rect.width,
      maxHeight: metrics.cappedMaxHeight,
      optionsMaxHeight: metrics.optionsMaxHeight,
      openUpward: metrics.openUpward,
      zIndex: portalZ,
    })
  }, [
    filteredOptions.length,
    forceUpward,
    hideSearch,
    maxHeight,
    maxVisibleOptions,
    options.length,
    usePortal,
  ])

  useLayoutEffect(() => {
    if (!isOpen || !usePortal) return

    updateMenuPosition()

    const handleReposition = () => updateMenuPosition()
    window.addEventListener('resize', handleReposition)

    const scrollRoots = new Set<EventTarget>([window])
    let el: HTMLElement | null = dropdownRef.current?.parentElement ?? null
    while (el) {
      const st = window.getComputedStyle(el)
      if (/(auto|scroll)/.test(`${st.overflowY}${st.overflowX}${st.overflow}`)) {
        scrollRoots.add(el)
      }
      el = el.parentElement
    }
    scrollRoots.forEach((target) => {
      target.addEventListener('scroll', handleReposition, true)
    })

    return () => {
      window.removeEventListener('resize', handleReposition)
      scrollRoots.forEach((target) => {
        target.removeEventListener('scroll', handleReposition, true)
      })
    }
  }, [isOpen, usePortal, updateMenuPosition])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop React-tree bubbling (incl. portals) so the same handler on an
    // ancestor — e.g. root + search input — does not fire twice on Enter.
    e.stopPropagation()

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleToggle()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex((prev) => Math.min(prev + 1, visibleOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        if (!onSearchChange) setInternalSearchQuery('')
        setTimeout(() => {
          triggerRef.current?.focus()
        }, 0)
        break
      case ' ':
        if (hideSearch) {
          e.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < visibleOptions.length) {
            const opt = visibleOptions[focusedIndex]
            if (!opt.disabled) handleSelect(opt.value)
          }
        }
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < visibleOptions.length) {
          const opt = visibleOptions[focusedIndex]
          if (!opt.disabled) handleSelect(opt.value)
        } else if (visibleOptions.length === 1) {
          const opt = visibleOptions[0]
          if (!opt.disabled) handleSelect(opt.value)
        }
        break
    }
  }

  const handleToggle = () => {
    if (disabled) return
    if (!isOpen) {
      updateMenuPosition()
      if (!onSearchChange) setInternalSearchQuery('')
      onOpen?.()
    }
    setIsOpen(!isOpen)
  }

  const handleSearchChange = (val: string) => {
    setInternalSearchQuery(val)
    if (onSearchChange) onSearchChange(val)
  }

  const renderMenu = () => {
    if (!isOpen || !menuPosition) return null

    const menuTop = resolveSearchableMenuTop(usePortal, menuPosition)
    const menuBody = renderSearchableDropdownMenuBody({
      isLoading,
      filteredOptionsLength: filteredOptions.length,
      menuPosition,
      visibleOptions,
      value,
      focusedIndex,
      isLoadingMore,
      handleListScroll,
      handleSelect,
      handleKeyDown,
    })

    const menuContent = (
      <div
        ref={menuRef}
        className="searchable-dropdown__menu"
        style={{
          position: usePortal ? 'fixed' : 'absolute',
          top: menuTop,
          bottom:
            !usePortal && menuPosition.openUpward ? 'calc(100% + 4px)' : 'auto',
          left: usePortal ? menuPosition.left : 0,
          width: usePortal ? menuPosition.width : '100%',
          maxHeight: menuPosition.maxHeight,
          zIndex: usePortal ? menuPosition.zIndex : 100,
          transform: usePortal && menuPosition.openUpward ? 'translateY(-100%)' : undefined,
        }}
      >
        {!hideSearch && (
          <div className="searchable-dropdown__search">
            <Search size={14} className="searchable-dropdown__search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="searchable-dropdown__search-input"
              placeholder={searchPlaceholder}
              value={internalSearchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-expanded={isOpen}
              aria-autocomplete="list"
              aria-controls="searchable-dropdown-listbox"
              aria-activedescendant={
                isOpen && !hideSearch && focusedIndex >= 0 ? `option-${focusedIndex}` : undefined
              }
            />
            {internalSearchQuery && (
              <button
                type="button"
                className="searchable-dropdown__search-clear"
                onClick={(e) => {
                  e.stopPropagation()
                  handleSearchChange('')
                  searchInputRef.current?.focus()
                }}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {menuBody}
      </div>
    )

    if (usePortal) {
      return createPortal(menuContent, document.body)
    }

    return menuContent
  }

  let selectedValueTitle: string | undefined
  if (selectedOption) {
    if (selectedOption.labelStr) {
      selectedValueTitle = selectedOption.labelStr
    } else if (typeof selectedOption.label === 'string') {
      selectedValueTitle = selectedOption.label
    }
  }

  return (
    <div className={`searchable-dropdown ${className}`} ref={dropdownRef}>
      <div className="searchable-dropdown__trigger-wrapper">
        <button
          ref={triggerRef}
          type="button"
          className={`searchable-dropdown__trigger ${isOpen ? 'searchable-dropdown__trigger--open' : ''} ${disabled ? 'searchable-dropdown__trigger--disabled' : ''}`}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? "searchable-dropdown-listbox" : undefined}
          aria-activedescendant={
            isOpen && hideSearch && focusedIndex >= 0 ? `option-${focusedIndex}` : undefined
          }
          aria-label={
            selectedOption ? `Selected: ${selectedOption.label}` : `Select ${placeholder}`
          }
          title={cleanTooltip(title || selectedOption?.labelStr || (typeof selectedOption?.label === 'string' ? selectedOption.label : undefined))}
        >
          <span
            className={
              selectedOption ? 'searchable-dropdown__value' : 'searchable-dropdown__placeholder'
            }
            title={cleanTooltip(selectedValueTitle)}
          >
            {selectedOption ? renderOptionLabel(selectedOption.label) : placeholder}
          </span>
          {!hideArrow && (
            <ChevronDown
              size={14}
              className={`searchable-dropdown__arrow ${isOpen ? 'searchable-dropdown__arrow--rotated' : ''}`}
            />
          )}
        </button>
        {value && !disabled && clearable && (
          <button
            type="button"
            className="searchable-dropdown__clear"
            onClick={handleClear}
            aria-label="Clear selection"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {renderMenu()}
    </div>
  )
}

export default SearchableDropdown
